import React, { useState, useEffect, useRef } from 'react';
import { Music, MapPin, Mail, Download, ExternalLink, Image, FileText, Users, Loader2, Play, Pause } from 'lucide-react';
import '../../app-background.css';

export default function PublicPressKit({ publicId }) {
  const [pressKit, setPressKit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [playingId, setPlayingId] = useState(null);
  const [audioProgress, setAudioProgress] = useState({});
  const audioRef = useRef(null);

  useEffect(() => {
    const fetchPressKit = async () => {
      try {
        setLoading(true); setError('');
        const response = await fetch(
          'https://europe-west1-tapsody-7f358.cloudfunctions.net/getPublicPressKit',
          { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ publicId }) }
        );
        const data = await response.json();
        if (data.error) { setError(data.error); } else { setPressKit(data); }
      } catch (err) { setError('Failed to load press kit'); }
      finally { setLoading(false); }
    };
    if (publicId) fetchPressKit();
  }, [publicId]);

  const playAudio = (songId, audioUrl) => {
    if (playingId === songId) {
      if (audioRef.current) audioRef.current.pause();
      setPlayingId(null);
    } else {
      if (audioRef.current) audioRef.current.pause();
      audioRef.current = new Audio(audioUrl);
      audioRef.current.play();
      setPlayingId(songId);
      audioRef.current.ontimeupdate = () => {
        const progress = (audioRef.current.currentTime / audioRef.current.duration) * 100;
        setAudioProgress(prev => ({ ...prev, [songId]: progress }));
      };
      audioRef.current.onended = () => {
        setPlayingId(null);
        setAudioProgress(prev => ({ ...prev, [songId]: 0 }));
      };
    }
  };

  useEffect(() => {
    return () => { if (audioRef.current) audioRef.current.pause(); };
  }, []);

  // ── Shared wrapper with orbs ──────────────────────────────
  const PageShell = ({ children }) => (
    <div className="min-h-screen app-bg text-white">
      <div className="app-bg-orbs">
        <div className="app-bg-orb app-bg-orb-aqua" />
        <div className="app-bg-orb app-bg-orb-magenta" />
      </div>
      {children}
    </div>
  );

  // ── Loading ───────────────────────────────────────────────
  if (loading) {
    return (
      <PageShell>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-brand animate-spin mx-auto mb-4" />
            <p className="text-text-subtle">Loading press kit...</p>
          </div>
        </div>
      </PageShell>
    );
  }

  // ── Error ─────────────────────────────────────────────────
  if (error) {
    return (
      <PageShell>
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <div className="card-3d border border-surface-border-light rounded-card p-8">
              <Music className="w-16 h-16 text-text-subtle opacity-50 mx-auto mb-4" />
              <h1 className="text-2xl font-bold mb-2">Press Kit Not Found</h1>
              <p className="text-text-subtle mb-6">{error}</p>
              <a href="https://tapsody.app" className="inline-block bg-brand hover:bg-brand-hover transition px-6 py-3 font-semibold rounded-pill">
                Visit Tapsody
              </a>
            </div>
          </div>
        </div>
      </PageShell>
    );
  }

  if (!pressKit) return null;

  const { band, bandDocs } = pressKit;

  // ── Helpers ───────────────────────────────────────────────
  const DownloadBtn = ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="bg-brand hover:bg-brand-hover transition px-4 py-2 font-semibold text-sm flex items-center gap-2 rounded-pill"
    >
      <Download className="w-4 h-4" />
      {children}
    </a>
  );

  const SectionHeading = ({ icon: Icon, children }) => (
    <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
      <Icon className="w-6 h-6 text-brand" />
      {children}
    </h2>
  );

  return (
    <PageShell>
      {/* Header */}
      <header className="border-b border-surface-border-light backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <a href="https://tapsody.app" className="flex items-center gap-2">
            <img src="/app/tapsody-logo.png" alt="Tapsody" className="h-8" />
          </a>
          <span className="text-xs text-text-subtle">Electronic Press Kit</span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
        {/* Band Header */}
        <div className="text-center mb-10">
          {bandDocs?.bandPhoto ? (
            <img
              src={bandDocs.bandPhoto.fileURL}
              alt={band.name}
              className="w-48 h-48 sm:w-64 sm:h-64 object-cover rounded-full mx-auto mb-6 border-4 border-brand shadow-lg shadow-brand/20"
            />
          ) : (
            <div className="w-48 h-48 sm:w-64 sm:h-64 card-3d rounded-full mx-auto mb-6 flex items-center justify-center border-4 border-surface-border-light">
              <Users className="w-20 h-20 text-text-subtle opacity-50" />
            </div>
          )}

          <h1 className="text-4xl sm:text-5xl font-bold mb-3 tracking-title">{band.name}</h1>

          {(band.location || band.region) && (
            <p className="text-text-subtle flex items-center justify-center gap-2 mb-3">
              <MapPin className="w-4 h-4" />
              {[band.location, band.region].filter(Boolean).join(', ')}
            </p>
          )}

          {band.genres?.length > 0 && (
            <div className="flex flex-wrap justify-center gap-2 mt-4">
              {band.genres.map(genre => (
                <span key={genre} className="bg-brand/20 text-brand text-sm px-3 py-1 rounded-full">
                  {genre}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Demo Songs */}
        {pressKit.demoSongs?.length > 0 && (
          <section className="mb-10">
            <SectionHeading icon={Music}>Listen</SectionHeading>
            <div className="space-y-3">
              {pressKit.demoSongs.map((song) => (
                <div key={song.id} className="card-3d border border-surface-border-light rounded-card p-4">
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => playAudio(song.id, song.audioUrl)}
                      className="w-12 h-12 bg-brand hover:bg-brand-hover transition rounded-full flex items-center justify-center flex-shrink-0"
                    >
                      {playingId === song.id
                        ? <Pause className="w-5 h-5 text-white" />
                        : <Play className="w-5 h-5 text-white ml-1" />
                      }
                    </button>

                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-lg truncate">{song.name}</p>
                      {song.versionNumber && (
                        <p className="text-sm text-text-subtle truncate">Version {song.versionNumber}</p>
                      )}
                      <div className="mt-2 bg-white/10 rounded-full h-1.5 overflow-hidden">
                        <div
                          className="bg-gradient-to-r from-brand to-brand-magenta h-full transition-all duration-200"
                          style={{ width: `${audioProgress[song.id] || 0}%` }}
                        />
                      </div>
                    </div>

                    <a
                      href={song.audioUrl}
                      download={`${song.name}.mp3`}
                      className="bg-white/10 hover:bg-white/20 transition p-2 rounded-full flex-shrink-0"
                      title="Download"
                    >
                      <Download className="w-4 h-4 text-white" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Biography */}
        {(bandDocs?.bioSmall || bandDocs?.bioMedium || bandDocs?.bioLarge) && (
          <section className="mb-10">
            <SectionHeading icon={FileText}>Biography</SectionHeading>
            <div className="card-3d border border-surface-border-light rounded-card p-6">
              <div className="flex flex-wrap gap-3">
                {bandDocs.bioSmall && <DownloadBtn href={bandDocs.bioSmall.fileURL}>Short Bio</DownloadBtn>}
                {bandDocs.bioMedium && <DownloadBtn href={bandDocs.bioMedium.fileURL}>Medium Bio</DownloadBtn>}
                {bandDocs.bioLarge && <DownloadBtn href={bandDocs.bioLarge.fileURL}>Full Bio</DownloadBtn>}
              </div>
            </div>
          </section>
        )}

        {/* Live Photos */}
        {bandDocs?.livePhotos?.length > 0 && (
          <section className="mb-10">
            <SectionHeading icon={Image}>Live Photos</SectionHeading>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {bandDocs.livePhotos.map((photo, index) => (
                <a
                  key={index}
                  href={photo.fileURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative aspect-square card-3d rounded-card overflow-hidden"
                >
                  <img
                    src={photo.fileURL}
                    alt={`Live performance ${index + 1}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition flex items-center justify-center">
                    <ExternalLink className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition" />
                  </div>
                </a>
              ))}
            </div>
          </section>
        )}

        {/* Press Photo */}
        {bandDocs?.bandPhoto && (
          <section className="mb-10">
            <SectionHeading icon={Image}>Press Photo</SectionHeading>
            <div className="card-3d border border-surface-border-light rounded-card p-6">
              <DownloadBtn href={bandDocs.bandPhoto.fileURL}>Download High-Res Photo</DownloadBtn>
            </div>
          </section>
        )}

        {/* Technical Documents */}
        {((bandDocs?.rider?.length > 0) || (bandDocs?.gearList?.length > 0)) && (
          <section className="mb-10">
            <SectionHeading icon={FileText}>Technical Documents</SectionHeading>
            <div className="card-3d border border-surface-border-light rounded-card p-6">
              <div className="flex flex-wrap gap-3">
                {bandDocs.rider?.map((doc, index) => (
                  <DownloadBtn key={`rider-${index}`} href={doc.fileURL}>
                    Rider {bandDocs.rider.length > 1 ? `(${index + 1})` : ''}
                  </DownloadBtn>
                ))}
                {bandDocs.gearList?.map((doc, index) => (
                  <DownloadBtn key={`gear-${index}`} href={doc.fileURL}>
                    Gear List {bandDocs.gearList.length > 1 ? `(${index + 1})` : ''}
                  </DownloadBtn>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Contact */}
        {((bandDocs?.musicLabel?.name || bandDocs?.musicLabel?.contact) ||
          (bandDocs?.booker?.name || bandDocs?.booker?.contact)) && (
          <section className="mb-10">
            <SectionHeading icon={Mail}>Contact</SectionHeading>
            <div className="card-3d border border-surface-border-light rounded-card p-6">
              <div className="grid sm:grid-cols-2 gap-6">
                {(bandDocs?.musicLabel?.name || bandDocs?.musicLabel?.contact) && (
                  <div>
                    <h3 className="font-semibold text-text-subtle text-sm mb-2">Music Label</h3>
                    {bandDocs.musicLabel.name && <p className="font-semibold">{bandDocs.musicLabel.name}</p>}
                    {bandDocs.musicLabel.contact && <p className="text-text-muted text-sm">{bandDocs.musicLabel.contact}</p>}
                  </div>
                )}
                {(bandDocs?.booker?.name || bandDocs?.booker?.contact) && (
                  <div>
                    <h3 className="font-semibold text-text-subtle text-sm mb-2">Booking</h3>
                    {bandDocs.booker.name && <p className="font-semibold">{bandDocs.booker.name}</p>}
                    {bandDocs.booker.contact && <p className="text-text-muted text-sm">{bandDocs.booker.contact}</p>}
                  </div>
                )}
              </div>
            </div>
          </section>
        )}
      </main>

      {/* Footer */}
      <footer className="mt-12 pt-6 border-t border-surface-border-light pb-8">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-text-subtle text-sm">
            Press kit powered by{' '}
            <a href="https://tapsody.app" className="text-brand hover:underline">tapsody.app</a>
          </p>
        </div>
      </footer>
    </PageShell>
  );
}
